'use client';

import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTheme } from '@emotion/react';
import { Page } from '../../models';
import { ConfigTypes } from '../../models/config';
import { http } from '../../lib/api/http';
import { V2 } from '../../lib/api/urls';
import { DEVICE_SCREENS_MAX_STRING } from '../../lib/consts';
import { Link } from '../../components/styled/Link';

type CookieAgreementContextType = {
  showPopUp: boolean;
  agreementPage: Page | null;
  accept: () => void;
};

export const CookieAgreementContext = createContext<
  CookieAgreementContextType | undefined
>(undefined);

export function useCookieAgreement() {
  const context = useContext(CookieAgreementContext);
  if (!context)
    throw new Error(
      'useCookieAgreement must be used inside <CookieAgreementProvider>'
    );
  return context;
}
function CookieAgreementModal() {
  const theme = useTheme();
  const { accept, agreementPage } = useCookieAgreement();
  return (
    <div
      css={{
        fontFamily: 'Golos',
        fontSize: 14,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',

        position: 'fixed',
        bottom: 40,
        right: 40,
        zIndex: 10000,

        width: 404,
        padding: '16px 20px',
        gap: 24,

        background: 'rgba(232, 232, 232, 0.9)',
        backdropFilter: 'blur(4px)',
        borderRadius: 24,

        [DEVICE_SCREENS_MAX_STRING.md]: {
          fontSize: 12,
          bottom: 0,
          right: 0,
          width: '100%',
          padding: '8px 12px',
          borderRadius: 0,
        },
      }}
    >
      <div css={{ color: theme.colors.textGray }}>
        Мы используем{' '}
        <Link
          href={`/pages/${agreementPage?.slug}/`}
          css={{
            textDecoration: 'underline',
            ':hover': { color: '#FF4A2E' },
          }}
        >
          рекомендации и куки
        </Link>{' '}
        для вашего удобства на сайте
      </div>
      <button
        type='button'
        css={{
          boxSizing: 'border-box',
          cursor: 'pointer',

          fontSize: 15,
          color: theme.colors.black,
          background: theme.colors.white,

          minWidth: 72,
          minHeight: 48,
          borderRadius: 16,

          [DEVICE_SCREENS_MAX_STRING.md]: {
            borderRadius: 12,
            fontSize: 11,
            minWidth: 55,
            minHeight: 36,
          },
        }}
        onClick={accept}
      >
        ОК
      </button>
    </div>
  );
}

export function CookieAgreementProvider({ children }: PropsWithChildren) {
  const [showPopUp, setShowPopUp] = useState(false);
  const [agreementPage, setAgreementPage] = useState<Page | null>(null);

  const accept = () => {
    localStorage.setItem('user-agree-term', '1');
    setShowPopUp(false);
  };

  useEffect(() => {
    const userAgreed = Number(localStorage.getItem('user-agree-term')) === 1;
    if (!userAgreed) {
      if (sessionStorage.getItem('agreementPage')) {
        setAgreementPage(
          JSON.parse(sessionStorage.getItem('agreementPage') || 'null')
        );
      } else {
        http.get(V2.config, {}).then((config: ConfigTypes) => {
          const showCookieWarningRequest = !!config.find(
            (item) => item.name === 'SHOW_COOKIE_WARNING'
          )?.value;
          http
            .get(V2.pages, { unique_content: 'agreement' })
            .then((pages: { results: Page[] }) => {
              if (pages.results.length > 0 && showCookieWarningRequest) {
                setAgreementPage(pages.results[0]);
                sessionStorage.setItem(
                  'agreementPage',
                  JSON.stringify(pages.results[0])
                );
              }
            });
        });
      }
    }
  }, []);

  useEffect(() => {
    if (agreementPage) {
      setShowPopUp(true);
    }
  }, [agreementPage]);

  const value = useMemo(
    () => ({ showPopUp, accept, agreementPage }),
    [showPopUp, agreementPage]
  );

  return (
    <CookieAgreementContext.Provider value={value}>
      {children}
      {showPopUp && <CookieAgreementModal />}
    </CookieAgreementContext.Provider>
  );
}
